import React, { FC, useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Avatar,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Box,
  Button,
  TextField,
  MenuItem,
} from "@material-ui/core";
import { Link, useHistory, useLocation } from "react-router-dom";
import { X } from "lucide-react";

import {
  searchAll,
  getFileByIdAPI,
} from "../../../../api/userApi";
import useInViewPort from "../../../../hooks/interationObserver";
import { showToast } from "../../../../utils/toast";
import videoIconSvg from "../../../../assets/videoIcon.svg";
import defaultAvatars from "../../../../assets/defaultAvatars.svg";
import SuggestionIcon from "../../../../assets/SuggestionIcon";
import VerifiedShieldHelpIcon from "../../../../assets/VerifiedShieldHelpIcon";
import {
  AdvancedCreatorFilters,
  ADVANCED_FILTER_KEYS,
  defaultAdvancedCreatorFilters,
  parseAdvancedFiltersFromSearch,
  serializeAdvancedFilters,
} from "./filtering";

enum FollowStatus {
  NOT_FOLLOWING = "not_following",
  FOLLOWING = "following",
  PENDING = "pending",
}

export interface CreatorSuggestion {
  _id: string;
  profileImg: string;
  firstName: string;
  lastName: string;
  username: string;
  isSubs?: boolean;
  isSubscribed?: boolean;
  follow: {
    followUserId: string;
    accepted: boolean;
  } | null;
  userType?: {
    _id: string;
    title: "viewer" | "creator";
    description?: string;
  };
}

type SuggestionWithFollow = CreatorSuggestion & {
  followStatus: FollowStatus;
};

type ApiResponse = {
  items: CreatorSuggestion[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
};

interface CreatorSuggestionsModalProps {
  open: boolean;
  onClose: () => void;
  canUsePremiumFilters: boolean;
}

const CreatorSuggestionsModal: FC<CreatorSuggestionsModalProps> = ({
  open,
  onClose,
  canUsePremiumFilters,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const lastCreatorRef = useRef<HTMLElement | undefined>(undefined);
  const inView = useInViewPort(lastCreatorRef, { threshold: 0.5 });
  const [suggestions, setSuggestions] = useState<SuggestionWithFollow[]>([]);
  const [mediaUrls, setMediaUrls] = useState<{ [key: string]: string }>({});
  const [, setPage] = useState<number>(1);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedCreatorFilters>(
    defaultAdvancedCreatorFilters
  );

  const fetchMediaUrl = async (mediaUrl: string) => {
    if (!mediaUrl) return videoIconSvg;
    // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
    if (mediaUrl.includes("lh3.googleusercontent.com")) {
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }
    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }
    const fileId = mediaUrl.split("/api/files/")[1];
    if (!fileId) return videoIconSvg;

    try {
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.error(`Error fetching avatar for ${mediaUrl}:`, err);
      showToast("Failed to load creator avatar", "error");
      return videoIconSvg;
    }
  };

  const getFollowStatus = (
    follow: CreatorSuggestion["follow"]
  ): FollowStatus => {
    if (!follow) {
      return FollowStatus.NOT_FOLLOWING;
    } else if (follow.accepted === true) {
      return FollowStatus.FOLLOWING;
    } else {
      return FollowStatus.PENDING;
    }
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const fetchSuggestions = async (currentPage: number) => {
    if (isLoading) return;
    setLoading(true);

    try {
      const serializedFilters = serializeAdvancedFilters(advancedFilters);
      const data = await searchAll("", {
        scope: "creators",
        page: currentPage,
        perPage: 12,
        excludeViewers: true,
        ...serializedFilters,
      });
      const { items, totalPages } = data as ApiResponse;

      const normalized = items
        .map((s: CreatorSuggestion) => ({
          _id: s._id,
          firstName: s.firstName,
          lastName: s.lastName,
          username: s.username,
          profileImg: s.profileImg,
          follow: s.follow,
          userType: s.userType,
          // API може віддавати різні прапорці: isSubs / isSubscribed / hasSubscribe
          isSubs: Boolean(
            (s as any).isSubs || (s as any).hasSubscribe || (s as any).isSubscribed
          ),
          isSubscribed: Boolean(
            (s as any).isSubscribed || (s as any).hasSubscribe || (s as any).isSubs
          ),
          followStatus: getFollowStatus(s.follow),
        }))
        .filter(s => {
          if (!s.userType) {
            return true;
          }
          const isViewer = s.userType.title === "viewer";
          return !isViewer;
        }) as SuggestionWithFollow[];

      const shuffledSuggestions = shuffleArray(normalized);

      setSuggestions(prev => {
        const newSuggestions = [
          ...prev,
          ...shuffledSuggestions.filter(s => !prev.some(p => p._id === s._id)),
        ];
        return newSuggestions;
      });

      setHasMore(currentPage < totalPages);
    } catch (err) {
      console.error("Modal: Error fetching suggestions:", err);
      showToast(
        (err as Error)?.message || "Error loading suggestions!",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setAdvancedFilters(parseAdvancedFiltersFromSearch(location.search));
      setSuggestions([]);
      setPage(1);
      setHasMore(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, location.search]);

  useEffect(() => {
    if (!open) return;
    setSuggestions([]);
    setPage(1);
    setHasMore(true);
    fetchSuggestions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, canUsePremiumFilters]);

  // Load more when scrolling to bottom
  useEffect(() => {
    if (inView && hasMore && !isLoading && open) {
      setPage(prev => {
        const nextPage = prev + 1;
        fetchSuggestions(nextPage);
        return nextPage;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, hasMore, isLoading, open]);

  // Fetch media URLs
  useEffect(() => {
    suggestions.forEach(creator => {
      if (creator.profileImg && !mediaUrls[creator.profileImg]) {
        fetchMediaUrl(creator.profileImg);
      }
    });
  }, [suggestions, mediaUrls]);

  const handleFilterChange = (field: keyof AdvancedCreatorFilters, value: string) => {
    setAdvancedFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setAdvancedFilters(defaultAdvancedCreatorFilters);
  };

  const goToUpgrade = () => {
    onClose();
    history.push("/subscriptions");
  };

  const applyAdvancedFiltersToUrl = () => {
    const serialized = serializeAdvancedFilters(advancedFilters);
    const params = new URLSearchParams(location.search);
    ADVANCED_FILTER_KEYS.forEach(key => params.delete(key));
    Object.entries(serialized).forEach(([key, value]) => {
      params.set(key, String(value));
    });
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  const textFieldBaseProps = {
    InputLabelProps: { style: { color: "#FFFFFFB2" } },
    InputProps: { style: { color: "#FFFFFF" } },
    FormHelperTextProps: { style: { color: "#FFFFFF80" } },
  } as const;

  const selectTextFieldProps = {
    ...textFieldBaseProps,
    SelectProps: {
      MenuProps: {
        PaperProps: {
          style: {
            background: "#1b1b1b",
            color: "#fff",
          },
        },
      },
      style: { color: "#FFFFFF" },
    },
  } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          backgroundColor: "#1a1a1a",
          color: "white",
          minHeight: isMobile ? "100vh" : "70vh",
          maxHeight: isMobile ? "100vh" : "80vh",
        },
      }}
    >
      <DialogTitle
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: isMobile ? "16px" : "24px 24px 16px 24px",
          borderBottom: "1px solid #333",
        }}
      >
        <Box
          width="210%"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box display="flex" alignItems="center">
            <SuggestionIcon style={{ color: "#FE5A97", marginRight: 12 }} />
            <Typography
              variant="h6"
              style={{
                color: "white",
                fontWeight: 600,
                fontSize: isMobile ? "18px" : "20px",
              }}
            >
              Creator Suggestions
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Button
              size="small"
              onClick={() => setAdvancedFiltersOpen(true)}
              style={{
                color: "#fff",
                textTransform: "none",
                border: "1px solid #FE5A9755",
                background: "#FE5A9714",
              }}
            >
              Advanced filters
            </Button>
            <IconButton
              onClick={onClose}
              style={{ color: "#FE5A97" }}
              size="small"
            >
              <X size={20} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent
        style={{
          padding: isMobile ? "16px" : "24px",
          paddingTop: "16px",
        }}
      >
        <Box
          style={{
            maxHeight: isMobile ? "calc(100vh - 120px)" : "60vh",
            overflowY: "auto",
            paddingRight: "8px",
          }}
        >
          {suggestions.map((creator, idx) => (
            <Link
              key={creator._id}
              to={`/${creator.username}`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 0",
                  borderBottom: "1px solid #333",
                  cursor: "pointer",
                }}
                ref={
                  idx === suggestions.length - 1
                    ? (lastCreatorRef as any)
                    : null
                }
              >
                <Box display="flex" alignItems="center" style={{ flex: 1 }}>
                  <Avatar
                    src={mediaUrls[creator.profileImg] || defaultAvatars}
                    style={{
                      width: isMobile ? 48 : 56,
                      height: isMobile ? 48 : 56,
                      marginRight: "12px",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body1"
                      style={{
                        color: "white",
                        fontWeight: 500,
                        fontSize: isMobile ? "14px" : "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {creator.firstName} {creator.lastName}
                      <VerifiedShieldHelpIcon
                        style={{ width: 16, height: 16 }}
                      />
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        color: "#999",
                        fontSize: isMobile ? "12px" : "14px",
                      }}
                    >
                      @{creator.username}
                    </Typography>
                  </Box>
                </Box>
              </div>
            </Link>
          ))}

          {isLoading && (
            <Box
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress size={24} style={{ color: "#FE5A97" }} />
            </Box>
          )}

          {!hasMore && suggestions.length > 0 && (
            <Box
              style={{
                textAlign: "center",
                padding: "20px",
                color: "#999",
                fontSize: "14px",
              }}
            >
              No more suggestions available
            </Box>
          )}

          {suggestions.length === 0 && !isLoading && (
            <Box
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#999",
              }}
            >
              <Typography variant="body1" style={{ marginBottom: "8px" }}>
                No suggestions available
              </Typography>
              <Typography variant="body2">
                Check back later for new creator suggestions
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <Dialog
        open={advancedFiltersOpen}
        onClose={() => setAdvancedFiltersOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            background: "#121212",
            color: "#fff",
            borderRadius: 14,
            border: "1px solid #2e2e2e",
          },
        }}
      >
        <DialogTitle style={{ borderBottom: "1px solid #2a2a2a" }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Advanced filters {canUsePremiumFilters ? "" : "(Premium)"}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setAdvancedFiltersOpen(false)}
              style={{ color: "#fff" }}
            >
              <X size={18} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 16 }}>
          {!canUsePremiumFilters && (
            <Box
              style={{
                border: "1px solid #47343d",
                borderRadius: 10,
                background: "#2a1b23",
                padding: 10,
                marginBottom: 12,
              }}
            >
              <Typography variant="body2" style={{ marginBottom: 8 }}>
                Unlock advanced creator filters with Premium.
              </Typography>
              <Button
                variant="contained"
                onClick={goToUpgrade}
                style={{
                  background: "#FE5A97",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Upgrade to Premium
              </Button>
            </Box>
          )}

          <Box
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: 10,
              opacity: canUsePremiumFilters ? 1 : 0.8,
            }}
          >
            <TextField variant="outlined" size="small" label="Location" {...textFieldBaseProps} value={advancedFilters.location} onChange={e => handleFilterChange("location", e.target.value)} />
            <TextField variant="outlined" size="small" label="Ethnicity" {...textFieldBaseProps} value={advancedFilters.ethnicity} onChange={e => handleFilterChange("ethnicity", e.target.value)} />
            <TextField variant="outlined" size="small" label="Age min" type="number" {...textFieldBaseProps} value={advancedFilters.ageMin} onChange={e => handleFilterChange("ageMin", e.target.value)} />
            <TextField variant="outlined" size="small" label="Age max" type="number" {...textFieldBaseProps} value={advancedFilters.ageMax} onChange={e => handleFilterChange("ageMax", e.target.value)} />
            <TextField variant="outlined" size="small" label="Body type" {...textFieldBaseProps} value={advancedFilters.bodyType} onChange={e => handleFilterChange("bodyType", e.target.value)} />
            <TextField variant="outlined" size="small" label="Orientation" {...textFieldBaseProps} value={advancedFilters.orientation} onChange={e => handleFilterChange("orientation", e.target.value)} />
            <TextField select variant="outlined" size="small" label="Online" {...selectTextFieldProps} value={advancedFilters.onlineStatus} onChange={e => handleFilterChange("onlineStatus", e.target.value)}>
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="online">Online</MenuItem>
              <MenuItem value="offline">Offline</MenuItem>
            </TextField>
            <TextField select variant="outlined" size="small" label="Verification" {...selectTextFieldProps} value={advancedFilters.verification} onChange={e => handleFilterChange("verification", e.target.value)}>
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="unverified">Unverified</MenuItem>
            </TextField>
            <TextField variant="outlined" size="small" label="Subscription price min" type="number" {...textFieldBaseProps} value={advancedFilters.subscriptionPriceMin} onChange={e => handleFilterChange("subscriptionPriceMin", e.target.value)} />
            <TextField variant="outlined" size="small" label="Subscription price max" type="number" {...textFieldBaseProps} value={advancedFilters.subscriptionPriceMax} onChange={e => handleFilterChange("subscriptionPriceMax", e.target.value)} />
            <TextField variant="outlined" size="small" label="PPV min" type="number" {...textFieldBaseProps} value={advancedFilters.ppvPriceMin} onChange={e => handleFilterChange("ppvPriceMin", e.target.value)} />
            <TextField variant="outlined" size="small" label="PPV max" type="number" {...textFieldBaseProps} value={advancedFilters.ppvPriceMax} onChange={e => handleFilterChange("ppvPriceMax", e.target.value)} />
            <TextField select variant="outlined" size="small" label="Creator status" {...selectTextFieldProps} value={advancedFilters.creatorStatus} onChange={e => handleFilterChange("creatorStatus", e.target.value)}>
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="trending">Trending</MenuItem>
              <MenuItem value="top">Top</MenuItem>
            </TextField>
            <TextField variant="outlined" size="small" label="Categories" {...textFieldBaseProps} value={advancedFilters.categories} onChange={e => handleFilterChange("categories", e.target.value)} helperText="Comma separated" />
          </Box>

          <Box display="flex" justifyContent="space-between" style={{ marginTop: 14 }}>
            <Button
              onClick={handleResetFilters}
              size="small"
              style={{ color: "#FE5A97", textTransform: "none" }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              style={{ background: "#FE5A97", color: "#fff", textTransform: "none" }}
              onClick={() => {
                if (!canUsePremiumFilters) {
                  showToast(
                    "Advanced filters are available for premium members",
                    "info"
                  );
                  return;
                }
                applyAdvancedFiltersToUrl();
                setAdvancedFiltersOpen(false);
              }}
            >
              Apply
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default CreatorSuggestionsModal;
